// localStorage-backed review state for the report viewer, keyed by a stable
// content hash of each discrepancy so a user's dismissals / pick-ups / edited
// comments survive a re-proof (which regenerates discrepancy ids). Mirrors
// the original report template's discrepancyKey() semantics.

import type { Discrepancy, PickupSnapshot } from "../api/types";

export type ReviewStatus = "none" | "dismissed" | "picked";

// User-facing flag categories. The engine emits finer types (substitution +
// mispronunciation are two flavours of the same "read it differently" event);
// the UI presents them as one "Misread" category. Keep the raw types under the
// hood (marks, signatures, AI analysis) — only the display groups them.
export type FlagCategory = "misread" | "missing" | "added" | "pause" | "noise" | "defect" | "manual";
export const FLAG_CATEGORIES: FlagCategory[] = ["misread", "missing", "added", "pause", "noise", "defect", "manual"];
export const CATEGORY_LABEL: Record<FlagCategory, string> = {
  misread: "Misread",
  missing: "Missing",
  added: "Added",
  pause: "Pauses",
  noise: "Noise",
  defect: "Defect",
  manual: "Manual",
};
export function categoryOf(type: string): FlagCategory {
  if (type === "missing" || type === "added" || type === "pause" || type === "manual") return type;
  return "misread"; // substitution, mispronunciation (and any legacy type)
}

// How much a flag changes the meaning vs. the manuscript — a manual triage
// aid the user sets per flag.
export type MeaningClass = "substantial" | "slight" | "none";

export const MEANING_LABEL: Record<MeaningClass, string> = {
  substantial: "Major",
  slight: "Minor",
  none: "No change",
};

export interface ReviewEntry {
  status?: ReviewStatus;
  title?: string; // user-edited headline override (real flags; manual flags keep title in their text)
  comment?: string; // user-edited comment override
  meaning?: MeaningClass; // meaning-impact classification
  category?: FlagCategory; // user-corrected category (overrides categoryOf(type))
  // Why a dismissed flag was cleared. "script": the narrator read what the
  // script's own note asked for ("Intended, per the script") - not a pickup.
  reason?: "script";
}

// Categories a real (engine-detected) flag can be re-assigned to. "manual" is
// excluded — that's reserved for user-created flags, not a correction target.
export const RECATEGORIZABLE: FlagCategory[] = ["misread", "missing", "added", "pause", "noise", "defect"];

export interface ManualFlag {
  id: string;
  type: "manual";
  page: number | null;
  start_time: number | null;
  end_time: number | null;
  comment: string;
  // Set when the flag was created from a manuscript text selection (vs the
  // by-ear "＋ Flag here" button): the selected fragment + the surrounding words
  // + the global token span. These make the flag highlight in the manuscript
  // and show its fragment highlighted in the Pickups Package, like an engine flag.
  script_text?: string;
  context_before?: string;
  context_after?: string;
  manuscript_token_indices?: number[];
}

export interface ReportState {
  marks: Record<string, ReviewEntry>;
  manual: Record<string, ManualFlag[]>; // keyed by chapterId
  // Content signatures dismissed in bulk ("dismiss all identical") — applies
  // title-wide across every chapter, independent of page/timecode.
  dismissed_signatures: string[];
  // Words heard in Find mode (a hit's clip played to its end), by chapter id:
  // the ticks that let a sweep through 200 names be done over several sittings.
  heard?: Record<string, number[]>;
}

const PREFIX = "proofbox.report.";

/** Per-instance key — unique to one flag (includes page + timecode). */
export function discrepancyKey(d: Discrepancy): string {
  const t = d.start_time != null ? d.start_time.toFixed(1) : "";
  return [
    d.type,
    d.page ?? "",
    (d.script_text || "").trim(),
    (d.heard_text || "").trim(),
    t,
  ].join("|");
}

/** Concise one-liner per finding — the report's title and the default pickup
 * title. Shared by the report viewer and the project-wide pickup sync. */
export function headlineFor(d: Discrepancy): string {
  const q = (s: string | undefined) => `“${(s || "").trim()}”`;
  if (d.credits) return `${d.credits === "end" ? "End" : "Opening"} credits: not in the manuscript`;
  switch (d.type) {
    case "substitution": return `Misread: ${q(d.script_text)} → ${q(d.heard_text)}`;
    case "mispronunciation": return `Misread: ${q(d.script_text)} → ${q(d.heard_text)}`;
    case "missing": return `Missing: ${q(d.script_text)}`;
    case "added": return `Added: ${q(d.heard_text)}`;
    case "pause": return d.heard_text || "Long pause";
    case "manual" as any: return d.heard_text || "Manual flag";
    default: return d.heard_text || d.script_text || "";
  }
}

/** Content signature — same for every identical flag across the title
 * (type + script + heard, normalised), ignoring page/timecode. Backs
 * "dismiss all identical". */
export function signatureOf(d: Discrepancy): string {
  const norm = (s: string | undefined) =>
    (s || "").trim().toLowerCase().replace(/\s+/g, " ");
  return [d.type, norm(d.script_text), norm(d.heard_text)].join("|");
}

// Compact flag shape returned by the project-level /flags endpoint — just
// enough to recompute review keys + signatures client-side.
export interface FlagLite {
  id: string;
  type: string;
  page: number | null;
  script_text: string;
  heard_text: string;
  start_time: number | null;
  filtered?: boolean;
  credits?: "opening" | "end";
}

export interface ReviewTally {
  total: number; // genuine (non-filtered) flags + manual flags
  picked: number;
  dismissed: number;
  unreviewed: number;
  filtered: number; // auto-filtered non-issues (excluded from total)
}

/** Aggregate review status across chapters, resolving effective status the
 * same way the report viewer does: explicit per-flag mark, else a title-wide
 * "dismiss all identical" signature rule. Auto-filtered flags don't count
 * toward the review total (they're hidden non-issues). Pure — takes the loaded
 * state, so it's trivially testable. */
export function tallyReview(
  state: ReportState,
  chapters: Array<{ chapter_id: string; flags: FlagLite[] }>,
  pickedKeys: Set<string> = new Set(),
  /** Server pickups, so a manual flag raised on the PHONE is counted. It has no
   *  entry in `state.manual` — it was a pickup from the moment it was tapped —
   *  so without this it appears in neither loop below and the take reads "0
   *  picked" while the report plainly shows it picked. */
  pickupList: Array<{ flag_key: string; chapter_id: string | null }> = [],
): ReviewTally {
  const dismissedSigs = new Set(state.dismissed_signatures || []);
  const t: ReviewTally = { total: 0, picked: 0, dismissed: 0, unreviewed: 0, filtered: 0 };

  // "picked" lives on the server (the Pickups store) AND is mirrored to a
  // durable local mark; either source counts. dismiss/comment/meaning stay in
  // localStorage. Picked wins over a dismiss on the same flag.
  const bump = (d: Discrepancy, key: string) => {
    let status: ReviewStatus = pickedKeys.has(key) || state.marks[key]?.status === "picked"
      ? "picked"
      : state.marks[key]?.status === "dismissed" || dismissedSigs.has(signatureOf(d))
        ? "dismissed"
        : "none";
    t.total++;
    if (status === "picked") t.picked++;
    else if (status === "dismissed") t.dismissed++;
    else t.unreviewed++;
  };

  // Manual flags reach this from two directions — a local entry (raised in the
  // report viewer) and a server pickup (raised on the phone) — and a flag
  // raised in the viewer and then picked is BOTH. Counted keys dedupe them.
  const counted = new Set<string>();

  for (const ch of chapters) {
    for (const f of ch.flags) {
      if (f.filtered) { t.filtered++; continue; }
      const d = f as unknown as Discrepancy;
      const key = discrepancyKey(d);
      counted.add(key);
      bump(d, key);
    }
    // Manual flags raised in the report viewer, keyed by chapter id.
    for (const mf of state.manual[ch.chapter_id] || []) {
      const title = (mf.comment || "").split("\n")[0];
      const d = {
        type: "manual", page: mf.page, start_time: mf.start_time,
        script_text: "", heard_text: title,
      } as unknown as Discrepancy;
      const key = `manual:${mf.id}`;
      counted.add(key);
      bump(d, key);
    }
    // Manual flags that exist ONLY as a pickup — raised on the phone, where
    // there is nothing to write a local entry into.
    for (const pk of pickupList) {
      if (pk.chapter_id !== ch.chapter_id) continue;
      if (!pk.flag_key.startsWith("manual:") || counted.has(pk.flag_key)) continue;
      counted.add(pk.flag_key);
      bump({ type: "manual", page: null, start_time: null, script_text: "", heard_text: "" } as unknown as Discrepancy,
           pk.flag_key);
    }
  }
  return t;
}

export function loadState(projectId: string): ReportState {
  try {
    const raw = localStorage.getItem(PREFIX + projectId);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        marks: parsed.marks || {},
        manual: parsed.manual || {},
        dismissed_signatures: parsed.dismissed_signatures || [],
        heard: parsed.heard || {},
      };
    }
  } catch {
    /* ignore */
  }
  return { marks: {}, manual: {}, dismissed_signatures: [], heard: {} };
}

export function saveState(projectId: string, state: ReportState): void {
  try {
    localStorage.setItem(PREFIX + projectId, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

/** Snapshots for every flag the user has locally marked "picked" that the
 * server doesn't have yet — so the Pickups Package can be healed project-wide
 * without opening each chapter's report (the report only syncs the chapter it's
 * showing). Engine flags come from the compact /flags list; manual flags from
 * local state. Context is left empty here; the Package enriches it from the live
 * chapter result when it loads. Idempotent: returns only what's missing. */
export function pendingPickups(
  flagsChapters: Array<{ chapter_id: string; name?: string | null; flags: FlagLite[] }>,
  chapterMeta: Map<string, { name?: string | null; audio_filename?: string | null }>,
  state: ReportState,
  serverKeys: Set<string>,
): PickupSnapshot[] {
  const out: PickupSnapshot[] = [];
  for (const ch of flagsChapters) {
    const meta = chapterMeta.get(ch.chapter_id);
    for (const f of ch.flags) {
      const d = f as unknown as Discrepancy;
      const key = discrepancyKey(d);
      if (state.marks[key]?.status !== "picked" || serverKeys.has(key)) continue;
      out.push({
        flag_key: key,
        chapter_id: ch.chapter_id,
        chapter_name: ch.name ?? meta?.name ?? null,
        audio_filename: meta?.audio_filename ?? null,
        flag_type: f.type,
        category: state.marks[key]?.category ?? categoryOf(f.type),
        page: f.page,
        script_text: f.script_text ?? "",
        heard_text: f.heard_text ?? "",
        context_before: "",
        context_after: "",
        title: state.marks[key]?.title ?? headlineFor(d),
        comment: state.marks[key]?.comment ?? "",
        start_time: f.start_time,
        end_time: null,
      });
    }
  }
  // Manual flags live only in localStorage, keyed `manual:<id>`. Only this
  // project's files: a state carried over from another book once held that
  // book's flags, and they were pushed here as this book's pickups.
  for (const [cid, mflags] of Object.entries(state.manual)) {
    const meta = chapterMeta.get(cid);
    if (!meta) continue;
    for (const mf of mflags) {
      const key = `manual:${mf.id}`;
      if (state.marks[key]?.status !== "picked" || serverKeys.has(key)) continue;
      const text = mf.comment || "";
      const nl = text.indexOf("\n");
      const title = (nl === -1 ? text : text.slice(0, nl)).trim() || "Manual flag";
      const body = (nl === -1 ? "" : text.slice(nl + 1)).trim();
      out.push({
        flag_key: key,
        chapter_id: cid,
        chapter_name: meta?.name ?? null,
        audio_filename: meta?.audio_filename ?? null,
        flag_type: "manual",
        category: state.marks[key]?.category ?? "manual",
        page: mf.page,
        // A selection-anchored flag carries the fragment + surrounding words, so
        // the Pickups Package highlights it (snapshot path: before · script · after).
        script_text: mf.script_text ?? "",
        heard_text: title,
        context_before: mf.context_before ?? "",
        context_after: mf.context_after ?? "",
        title: state.marks[key]?.title ?? title,
        comment: body,
        start_time: mf.start_time,
        end_time: mf.end_time,
      });
    }
  }
  return out;
}
